import { createSelector } from 'reselect';
import {
  getSummary,
  getFormatting,
  getAnalysis,
  getManagement,
  getManagementStatements,
} from '@/pages/CompanyReport/redux/selectors';
import { getPeople } from '@ducks/people/selectors';
import type { People } from '@ducks/people';
import { setDefaultMember } from '@ducks/people';
import type { SelectedStatement } from '@/pages/CompanyReport/redux/interface';
import {
  toTruncated,
  toCurrencyTruncated,
  toCurrency,
  toSignificant,
} from '@simplywallst/formatting';
import { formatterTenure } from '@/utilities/format';
import { get, minBy, maxBy } from 'lodash';

export interface ManagementStatements {
  isCEOCompensationAppropriate: SelectedStatement;
  isCEOCompensationChangeJustified: SelectedStatement;
  isManagementTeamSeasoned: SelectedStatement;
  isBoardSeasoned: SelectedStatement;
}

interface KeyMetric {
  value: string;
  title: string;
}

const getManagementKeyMetic = (
  managementTenure: number,
  boardTenure: number,
  leaderCompensation: number,
  reportingCurrencyISO: string
): KeyMetric => {
  let value = '';
  let title = '';
  if (managementTenure) {
    value = `${toTruncated(managementTenure, 1)}yrs`;
    title = 'Average management tenure';
  } else if (boardTenure) {
    value = `${toTruncated(boardTenure, 1)}yrs`;
    title = 'Average board tenure';
  } else if (leaderCompensation && leaderCompensation > 0) {
    value = toCurrencyTruncated(leaderCompensation, 0, reportingCurrencyISO);
    title = 'CEO Compensation';
  }
  return {
    value,
    title,
  };
};

interface ManagementSelector extends Omit<People, 'compensation' | 'hasCEO'> {
  compensation: string;
  formattedLeaderTenure: string;
  formattedBoardTenure: string;
  formattedManagementTenure: string;
  hasCEO: boolean;
}

const getManagementSelector = createSelector(
  [
    getSummary,
    getFormatting,
    getAnalysis,
    getManagement,
    getPeople,
    getManagementStatements,
  ],
  (summaryNext, formatting, analysis, management, people, statements) => {
    const boardManagementMapper = (personId: string) => {
      if (personId in people) {
        const person = people[personId];
        return {
          ...people[personId],
          formattedLeaderTenure: formatterTenure(person?.leaderTenure),
          formattedBoardTenure: formatterTenure(person?.boardTenure),
          formattedManagementTenure: formatterTenure(person?.managementTenure),
          compensation:
            people[personId].compensation > 0
              ? toCurrencyTruncated(
                  people[personId].compensation,
                  2,
                  formatting.reportingCurrencyISO
                )
              : '',
        } as ManagementSelector;
      }
      return null;
    };
    const mappedBoardMembers = management.boardMembers
      .map(boardManagementMapper)
      .filter(Boolean) as ManagementSelector[];

    const mappedManagementTeam = management.managementTeam
      .map(boardManagementMapper)
      .filter(Boolean) as ManagementSelector[]; // this filter should remove all falsey values

    const { [management.leaderId]: matchedLeader } = people;
    const leaderState = matchedLeader ? matchedLeader : setDefaultMember();
    const { legacyChartData } = analysis;
    const managementKeyMetics = getManagementKeyMetic(
      management.managementTenure,
      management.boardTenure,
      leaderState.compensation,
      formatting.reportingCurrencyISO
    );
    let formattedLeaderCompensation = '';
    try {
      if (
        typeof leaderState.compensation === 'number' &&
        leaderState.compensation > 0
      ) {
        formattedLeaderCompensation = toCurrency(
          leaderState.compensation,
          0,
          formatting.reportingCurrencyISO
        );
      }
    } catch (e) {
      // noop
    }

    /**
     * CEO Compensation
     */
    const totalCompensation = legacyChartData.ceoCompensationAnalysis.total;
    const totalCompensationMax = maxBy(totalCompensation, (n) => n[1]);
    const totalCompensationMin = minBy(totalCompensation, (n) => n[1]);

    let earnings: number[][] = [];

    const salary = legacyChartData.ceoCompensationAnalysis.salary;
    const salaryMax = maxBy(salary, (n) => n[1]);
    const salaryMin = minBy(salary, (n) => n[1]);

    let CEOCompensationDayZero: number | undefined = undefined;

    if (totalCompensation.length > 0) {
      CEOCompensationDayZero =
        totalCompensation[totalCompensation.length - 1][0];
      earnings = legacyChartData.pastNetIncome
        .sort((a, b) => a[0] - b[0])
        .filter((n) => n[0] >= totalCompensation[0][0]);
    }
    if (salary.length > 0) {
      if (CEOCompensationDayZero === undefined) {
        CEOCompensationDayZero = salary[salary.length - 1][0];
      }
      if (earnings.length === 0) {
        earnings = legacyChartData.pastNetIncome
          .sort((a, b) => a[0] - b[0])
          .filter((n) => n[0] >= salary[0][0]);
      }
    }
    if (earnings.length > 0) {
      if (CEOCompensationDayZero === undefined) {
        CEOCompensationDayZero = earnings[earnings.length - 1][0];
      }
      if (earnings.length === 0) {
        earnings = legacyChartData.pastNetIncome;
      }
    }

    const earningsMax = maxBy(earnings, (n) => n[1]);
    const earningsMin = minBy(earnings, (n) => n[1]);

    return {
      keyMetric: managementKeyMetics.value,
      keyMetricTitle: managementKeyMetics.title,
      board: mappedBoardMembers,
      boardAge: management.boardAge,
      companyId: summaryNext.companyId,
      hasCEO: leaderState.hasCEO || false,
      leaderAge: leaderState.age,
      leaderTenure: toSignificant(get(leaderState, 'tenure', 0), 1),
      leaderTenureFormatted: formatterTenure(leaderState.leaderTenure),
      boardTenureFormatted: formatterTenure(leaderState.boardTenure),
      managementTenureFormatted: formatterTenure(leaderState.managementTenure),
      leaderYearBorn: leaderState.yearBorn,
      leaderLastName: leaderState.lastName,
      leaderBiography: leaderState.biography,
      leaderSalutation: leaderState.salutation,
      leaderCompensation: formattedLeaderCompensation,
      companyName: summaryNext.shortName,
      currencyISO: formatting.reportingCurrencyISO,
      boardTenure: management.boardTenure,
      uniqueSymbol: summaryNext.uniqueSymbol,
      reportingUnit: formatting.reportingUnit,
      managementAge: management.managementAge,
      currencySymbol: formatting.reportingCurrencySymbol,
      managementTeam: mappedManagementTeam || [],
      managementProfit: legacyChartData.uncappedPastEps,
      managementTenure: management.managementTenure,
      statements: statements,
      /**
       * CEO Compensation
       */
      earnings,
      earningsMax: earningsMax ? earningsMax[1] : 0,
      earningsMin: earningsMin ? earningsMin[1] : 0,
      salary,
      totalCompensation,
      salaryMax: salaryMax ? salaryMax[1] : 0,
      salaryMin: salaryMin ? salaryMin[1] : 0,
      totalCompensationMax: totalCompensationMax ? totalCompensationMax[1] : 0,
      totalCompensationMin: totalCompensationMin ? totalCompensationMin[1] : 0,
      CEOCompensationDayZero,
    };
  }
);

export const getCEOCompensationProps = createSelector(
  [getManagementSelector],
  (management) => {
    return {
      currencyISO: management.currencyISO,
      reportingUnit: management.reportingUnit,
      earnings: management.earnings,
      earningsMax: management.earningsMax,
      earningsMin: management.earningsMin,
      salary: management.salary,
      totalCompensation: management.totalCompensation,
      salaryMax: management.salaryMax,
      salaryMin: management.salaryMin,
      totalCompensationMax: management.totalCompensationMax,
      totalCompensationMin: management.totalCompensationMin,
      dayZero: management.CEOCompensationDayZero,
    };
  }
);

export { getManagementSelector as default };
